import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { BadgePercent, Save, RefreshCw, TrendingUp } from "lucide-react";

const fmtAr = (n) => (Number(n) || 0).toLocaleString("fr-FR") + " Ar";
const fmtEur = (n) => (Number(n) || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR", minimumFractionDigits: 2 });

export const RateCardPanel = () => {
  const [data, setData] = useState(null);
  const [marginInput, setMarginInput] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/rate-card").then(({ data }) => {
    setData(data);
    setMarginInput(String(data.config.margin_pct ?? 100));
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const m = Math.max(0, Math.min(500, Number(marginInput) || 0));
      const { data } = await api.post("/admin/rate-card", { margin_pct: m, enabled: true });
      setData(data);
      toast.success(`Marge mise à jour : ×${(1 + m / 100).toFixed(2)}`);
    } catch { toast.error("Erreur"); }
    finally { setBusy(false); }
  };

  if (!data) return null;
  const p = data.preview;

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-5" data-testid="ratecard-panel">
      <div className="flex items-center gap-2 mb-1">
        <span className="font-display text-lg font-black inline-flex items-center gap-2"><BadgePercent size={16} className="text-emerald-400" /> Rate Card public</span>
        <span className="text-[10px] uppercase tracking-widest text-white/45">/annoncer</span>
      </div>
      <p className="text-xs text-white/55">
        Tarifs publics calculés depuis l'eCPM réel mesuré (Voamada Ads + AdSense). Plus tu as de trafic et de bonnes pubs, plus tes prix montent automatiquement.
      </p>

      {/* Stats baseline */}
      <div className="grid md:grid-cols-4 gap-2">
        <Mini label="eCPM marché" value={fmtEur(p.market_ecpm)} hint="Max(Voamada, AdSense)" highlight />
        <Mini label="eCPM revente" value={fmtEur(p.sell_ecpm)} hint="après marge" />
        <Mini label="Voamada Ads" value={fmtEur(p.voamada_ecpm)} hint="mesuré" />
        <Mini label="AdSense" value={fmtEur(p.adsense_ecpm)} hint="mesuré" />
      </div>

      {/* Margin slider */}
      <div className="rounded-xl border border-white/10 bg-black/30 p-4 space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <label className="text-[11px] uppercase tracking-widest text-white/55 font-bold">Marge sur l'eCPM marché</label>
          <span className="font-display text-2xl font-black text-emerald-400" data-testid="margin-value">+{marginInput}% (×{(1 + Number(marginInput)/100).toFixed(2)})</span>
        </div>
        <input type="range" min="0" max="300" step="10" value={marginInput} onChange={(e) => setMarginInput(e.target.value)} className="w-full accent-emerald-400" data-testid="margin-slider" />
        <div className="flex justify-between text-[10px] text-white/40">
          <span>0%</span><span>100% (recommandé)</span><span>300%</span>
        </div>
        <button onClick={save} disabled={busy} className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-50" data-testid="ratecard-save">
          <Save size={14} /> {busy ? "Enregistrement…" : "Enregistrer"}
        </button>
      </div>

      {/* Preview packs */}
      <div>
        <p className="text-[11px] uppercase tracking-widest text-white/45 font-bold mb-2 inline-flex items-center gap-1.5"><TrendingUp size={11} /> Aperçu live</p>
        <div className="grid md:grid-cols-3 gap-2">
          {p.packs.map((pk) => (
            <div key={pk.key} className={`rounded-xl border p-3 ${pk.highlight ? "border-brand-orange/40 bg-brand-orange/5" : "border-white/10 bg-black/20"}`} data-testid={`ratecard-preview-${pk.key}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="font-bold text-sm">{pk.name}</span>
                {pk.badge && <span className="text-[9px] px-1.5 py-0.5 rounded bg-brand-yellow text-brand-dark font-black">{pk.badge}</span>}
              </div>
              <p className="font-display text-xl font-black tabular-nums">{pk.price_ar_fmt} <span className="text-xs text-white/50 font-normal">Ar/mois</span></p>
              <p className="text-[10px] text-white/45">≈ {pk.volume_imp.toLocaleString("fr-FR")} imp · eCPM {pk.cpm_effective_eur} €</p>
              <p className="text-[10px] text-emerald-300/80 mt-0.5">≈ {fmtEur(pk.price_eur)} HT</p>
            </div>
          ))}
        </div>
      </div>

      <details className="rounded-xl border border-white/10 bg-black/30 p-3 text-xs text-white/65">
        <summary className="cursor-pointer text-white/55 font-bold uppercase tracking-widest">Comment ça marche ?</summary>
        <ol className="list-decimal pl-5 mt-2 space-y-1">
          <li>On mesure l'eCPM réel sur la plateforme (Voamada Ads + AdSense), on prend le max</li>
          <li>On applique ta marge (×2 par défaut) → eCPM revente</li>
          <li>On applique une remise volume (Pro −10%, Enterprise −25%) pour rester compétitif sur les gros packs</li>
          <li>Le prix final = eCPM × volume garanti / 1000, plancher à 5 €</li>
          <li>La page <code>/annoncer</code> affiche ces prix avec un badge "Tarifs basés sur les performances réelles"</li>
        </ol>
      </details>
    </div>
  );
};

const Mini = ({ label, value, hint, highlight }) => (
  <div className={`rounded-xl border p-2.5 ${highlight ? "border-emerald-500/30 bg-emerald-500/5" : "border-white/10 bg-black/20"}`}>
    <p className="text-[9px] uppercase tracking-widest text-white/45 font-bold">{label}</p>
    <p className={`font-display text-base font-black tabular-nums ${highlight ? "text-emerald-300" : ""}`}>{value}</p>
    <p className="text-[9px] text-white/35">{hint}</p>
  </div>
);

export default RateCardPanel;
